<template>
  <div class="form__element form__element--floating">
    <div class="form__input"
         :class="{
          'form__input--negative': inverted,
          'form__input--error': hasError}">
      <div class="form__input-main-wrapper">
        <input class="form__input-field"
               :aria-invalid="hasError ? 'true' : null"
               :aria-describedby="hasError ? `error-${id}-${inputId}` : null"
               :value="modelValue"
               @input="handleInput"
               @blur="handleBlur"
               :autocomplete="autocomplete"
               :type="type"
               :pattern="pattern"
               :id="id + '-' + inputId"
               :placeholder="placeholder">
        <span class="form__label">
          <label class="label"
                 :for="id + '-' + inputId">{{ label }}</label>
        </span>
        <button class="btn form__input-clear-btn btn--icon-only"
                v-if="modelValue && modelValue.length > 0"
                @click="clearInput"
                aria-label="Clear">
          <span class="btn__icon">
            <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-labelledby="title-yy2sljs29" role="presentation"><title id="title-yy2sljs29">Clear</title>
                <path d="M16.238 17 9 9.762 9.762 9 17 16.238 24.238 9l.762.762L17.762 17 25 24.238l-.762.762L17 17.762 9.762 25 9 24.238 16.238 17Z"></path>
              </svg>
            </span>
          </span>
        </button>
      </div>
    </div>
    <Error v-if="hasError"
           :inverted="inverted"
           :id="'error-' + id + '-' + inputId"
           :message="errorMessage" />
  </div>
</template>

<script setup>
import { ref, computed, watch, useId } from 'vue';
import Error from './Error.vue';

const props = defineProps({
  autocomplete: {
    type: [String, Boolean],
    default: 'off'
  },
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  pattern: {
    type: String,
    default: null,
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  rules: {
    type: Array,
    default: () => [],
  },
  forceValidation: {
    type: Boolean,
    default: false,
  },
  inverted: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:modelValue', 'validation-change']);

const internalValue = ref(props.modelValue);
const isTouched = ref(false);
const errorMessage = ref('');
const inputId = useId();

watch(() => props.modelValue, (newValue) => {
  internalValue.value = newValue;
});

const validate = () => {
  for (const rule of props.rules) {
    const result = rule(internalValue.value);
    if (typeof result === 'string') {
      errorMessage.value = result;
      emit('validation-change', false);
      return false;
    }
  }
  errorMessage.value = '';
  emit('validation-change', true);
  return true;
};

watch([internalValue, () => props.forceValidation, isTouched], () => {
  if (isTouched.value || props.forceValidation) {
    validate();
  } else {
    errorMessage.value = '';
  }
}, { immediate: true });


const handleInput = (event) => {
  internalValue.value = event.target.value;
  emit('update:modelValue', internalValue.value);
};

const handleBlur = () => {
  isTouched.value = true;
  validate();
};

const clearInput = () => {
  internalValue.value = '';
  emit('update:modelValue', '');
  isTouched.value = true;
};

const hasError = computed(() => (isTouched.value || props.forceValidation) && errorMessage.value !== '');
</script>